import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { isType } from '@sorare/core/src/lib/gql';

import { PlayMissionsDialog } from '#src/components/missions/PlayMissionsDialog/index.tsx';
import { PlayMissionsTaskRow } from '#src/components/missions/PlayTask/index.tsx';
import { TaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/index.tsx';
import { useTaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/useTaskClaimedDialogs.tsx';

import type { FeaturedPlayTask_taskInterface } from './__generated__/index.graphql.ts';

const Root = styled(Bezel)`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: var(--triple-unit) var(--double-unit);
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
`;

const TextureBackgroundWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  & > * {
    width: 100%;
    height: 100%;
  }
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgba(var(--c-rgb-black), 0.1) 0%,
      rgba(var(--c-rgb-black), 0.85) 100%
    );
  }
`;

type Props = {
  task?: FeaturedPlayTask_taskInterface | null;
  refetch?: () => void;
};

export const FeaturedPlayTask = ({ task, refetch }: Props) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    onClaim,
    loadingTaskId,
    pickerDialogTask,
    setPickerDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    rewardDialogTask,
    setRewardDialogTask,
  } = useTaskClaimedDialogs();

  if (!task) {
    return null;
  }

  return (
    <>
      <Root borderColor="var(--c-arena-primary)">
        <TextureBackgroundWrapper>
          <TextureBackground
            background="missions"
            mode="light"
            color="var(--c-arena-primary)"
            withDots
          />
        </TextureBackgroundWrapper>
        <PlayMissionsTaskRow
          task={task}
          onClaim={
            isType(task, 'Task')
              ? () => {
                  onClaim(task);
                  refetch?.();
                }
              : undefined
          }
          loading={loadingTaskId === task.id}
        />
        <Button
          size="medium"
          color="quaternary"
          onClick={() => setDialogOpen(true)}
        >
          <FormattedMessage
            defaultMessage="View all Play Missions"
            description="FeaturedPlayTask - View all Play Missions button"
          />
        </Button>
      </Root>
      <PlayMissionsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
      <TaskClaimedDialogs
        pickerDialogTask={pickerDialogTask}
        onPickerDialogClose={() => {
          setPickerDialogTask(null);
          refetch?.();
        }}
        revealPackCardsDialogReward={revealPackCardsDialogReward}
        onRevealPackCardsDialogClose={() => {
          setRevealPackCardsDialogReward(null);
          refetch?.();
        }}
        rewardDialogTask={rewardDialogTask}
        onRewardDialogClose={() => {
          setRewardDialogTask(null);
          refetch?.();
        }}
      />
    </>
  );
};

FeaturedPlayTask.fragments = {
  taskInterface: gql`
    fragment FeaturedPlayTask_taskInterface on TaskInterface {
      id
      ...PlayMissionsTaskRow_taskInterface
      ...TaskClaimedDialogs_taskInterface
    }
    ${PlayMissionsTaskRow.fragments.taskInterface}
    ${TaskClaimedDialogs.fragments.taskInterface}
  ` as TypedDocumentNode<FeaturedPlayTask_taskInterface>,
};
